import React, { Component } from 'react';
import { Drawer, Intent, Icon } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import colors from 'utils/colors';
import { DialogWrapper } from 'components/DialogWrapper';
import {
  DrawerHeader,
  FlexRow,
  BlueTextPointer,
  PlainButton,
} from 'components/styled-components';
import cloneDeep from 'lodash/cloneDeep';
import FormTextAreaGroup from 'components/forms/components/form-text-area-group';
import ErrorWrapper from 'components/forms/components/error-wrapper';
import { createQuestionnaireAnswer } from 'api/questionnaire';
import { AppToaster } from 'components/AppToaster';
import { AnswerDetails } from 'components/forms/questionnaire-management/answer-details';
import {
  BreadCrumb,
  QuestionLabel,
  Question,
  ScrollWrapper,
} from 'containers/Manage-Questionnaire/styled-components';

const initialState = {
  editMade: false,
  answer: '',
  score: 1,
  autoFail: false,
  closeWithoutSaving: false,
  answerError: false,
  scoreError: false,
  returnToParent: false,
};

class QuestionnaireAddAnswerDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = cloneDeep(initialState);
  }

  handleCloseDrawer = () => {
    if (this.state.editMade) {
      this.toggleCloseWithoutSaving();
    } else {
      this.resetState();
      this.props.closeDrawer();
    }
  };

  resetState = () => this.setState(cloneDeep(initialState));

  returnToParent = () => {
    if (this.props.isParentOpen) {
      this.handleCloseDrawer();
    } else {
      this.handleCloseDrawer();
      this.props.openParent();
    }
  };

  handleTextEdit = ({ target }) => {
    const { value } = target;
    this.setState({ answer: value, editMade: true });
    this.validateAnswerValue(value);
  };

  validateAnswerValue = answerValue => {
    if (answerValue.length > 0) {
      this.setState({ answerError: false });
    } else {
      this.setState({ answerError: true });
    }
  };

  handleAutoFailChange = () =>
    this.setState({ autoFail: !this.state.autoFail, editMade: true });

  handleScoreChange = score => {
    this.setState({ score, editMade: true });
    this.validateScore(score);
  };

  validateScore = score =>
    this.setState({ scoreError: score <= 0 ? true : false });

  toggleCloseWithoutSaving = () =>
    this.setState({ closeWithoutSaving: !this.state.closeWithoutSaving });

  handleCreateAnswer = () => {
    const { answer, score, autoFail } = this.state;
    createQuestionnaireAnswer(
      { answer, score, autoFail },
      this.props.question.questionID
    )
      .then(res => {
        AppToaster.show({
          message: res.data.message,
          intent: Intent.SUCCESS,
        });
        this.props.fetchQuestionnaireQuestions();
        this.setState({ editMade: false });
      })
      .catch(err => {
        AppToaster.show({
          message: err.response.data.message,
          intent: Intent.DANGER,
        });
      });
  };

  handleCreateLocalAnswer = () => {
    const { answer, score, autoFail } = this.state;
    this.props.handleCreateAnswer({ answer, score, autoFail });
    this.resetState();
    this.props.closeDrawer();
  };

  render() {
    const {
      answer,
      score,
      autoFail,
      editMade,
      answerError,
      scoreError,
    } = this.state;
    const { question, isOpen } = this.props;

    return (
      <Drawer
        isOpen={isOpen}
        onClose={this.handleCloseDrawer}
        hasBackdrop={!this.props.isParentOpen}
      >
        <DrawerHeader>
          Add Answer
          {editMade ? (
            <FlexRow fontSize="0.7rem" width="15rem">
              <BlueTextPointer onClick={this.toggleCloseWithoutSaving}>
                DISCARD
              </BlueTextPointer>
              <PlainButton
                onClick={
                  this.props.newQuestion
                    ? this.handleCreateLocalAnswer
                    : this.handleCreateAnswer
                }
                disabled={
                  answerError || scoreError || answer === '' || score === null
                }
              >
                SAVE CHANGES
              </PlainButton>
            </FlexRow>
          ) : (
            <Icon
              icon={IconNames.CROSS}
              iconSize={Icon.SIZE_LARGE}
              onClick={this.handleCloseDrawer}
              style={{ cursor: 'pointer' }}
            />
          )}
        </DrawerHeader>
        <BreadCrumb
          onClick={() => {
            if (editMade) {
              this.setState({
                returnToParent: true,
                closeWithoutSaving: true,
              });
            } else {
              this.returnToParent();
            }
          }}
        >
          <Icon icon={IconNames.CHEVRON_LEFT} iconSize={25} />
          <span>Edit Question</span>
        </BreadCrumb>
        <ScrollWrapper>
          <QuestionLabel>Question</QuestionLabel>
          <Question>
            {typeof question === 'string' ? question : question.question}
          </Question>
          {answerError && (
            <ErrorWrapper>The answer field cannot be blank.</ErrorWrapper>
          )}
          <FormTextAreaGroup
            label="Answer"
            requiredLabel={false}
            name="answer"
            fill={true}
            value={answer.answer}
            handleChange={this.handleTextEdit}
            style={{ background: colors.formBackground }}
          />
          {scoreError && (
            <ErrorWrapper>An answer must have a score.</ErrorWrapper>
          )}
          <AnswerDetails
            score={score}
            autoFail={autoFail}
            handleAutoFailChange={this.handleAutoFailChange}
            handleScoreChange={this.handleScoreChange}
          />
          <DialogWrapper
            isOpen={this.state.closeWithoutSaving}
            onClose={this.toggleCloseWithoutSaving}
            title="You have unsaved changes on this page"
            body="If you leave this page, all unsaved changes will be lost. Are you sure you want to leave this page?"
            confirm="DISCARD CHANGES"
            action={() => {
              if (this.state.returnToParent) {
                this.setState(
                  { editMade: false, closeWithoutSaving: false },
                  () => this.returnToParent()
                );
              } else {
                this.setState(
                  { editMade: false, closeWithoutSaving: false },
                  () => this.handleCloseDrawer()
                );
              }
            }}
          />
        </ScrollWrapper>
      </Drawer>
    );
  }
}

export default QuestionnaireAddAnswerDrawer;
